import axios from "axios";

import { baseUrl } from "../store/slices/auth";
import { ProgramMaterial } from "./program";

export type SelfCourseLessonType =
  | "dialog"
  | "video"
  | "material"
  | "lab"
  | "quiz"
  | "assignment";

export type SelfCourseQuizQuestion = {
  _id?: string;
  question: string;
  allowMultiple?: boolean;
  options: Array<{
    _id?: string;
    text: string;
    isCorrect?: boolean;
  }>;
};

export type SelfCourseLesson = {
  _id: string;
  title: string;
  type: SelfCourseLessonType;
  durationMinutes: number;
  content?: string;
  videoUrl?: string;
  passingScore?: number;
  materials?: ProgramMaterial[];
  questions?: SelfCourseQuizQuestion[];
};

export type SelfCourseModule = {
  _id: string;
  title: string;
  subtitle?: string;
  order: number;
  lessons: SelfCourseLesson[];
};

export type SelfCourse = {
  _id: string;
  title: string;
  slug: string;
  description: string;
  coverImage?: string;
  level?: string;
  status: "draft" | "published" | "archived";
  modules: SelfCourseModule[];
  enrollment?: SelfCourseEnrollment | null;
};

export type SelfCourseSubmission = {
  _id: string;
  lessonId: string;
  link: string;
  comment?: string;
  status: "submitted" | "reviewed" | "accepted" | "needs_revision";
  score?: number | null;
  adminComment?: string;
  submittedAt?: string;
  reviewedAt?: string | null;
};

export type SelfCourseEnrollment = {
  _id: string;
  courseId: string;
  userId:
    | string
    | {
        _id: string;
        username: string;
        telephone: string;
        email?: string;
        resume?: {
          name: string;
          url: string;
          mimeType?: string;
          size?: number;
        };
      };
  status: "active" | "completed";
  progress: number;
  lessons: Array<{
    lessonId: string;
    status: "not_started" | "completed" | "submitted";
    score?: number | null;
    completedAt?: string | null;
  }>;
  submissions: SelfCourseSubmission[];
  updatedAt?: string;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export const selfCoursesApi = {
  listMine: async () => {
    const response = await axios.get<{ courses: SelfCourse[] }>(
      `${baseUrl}self-courses`,
      { headers: authHeaders() }
    );
    return response.data.courses;
  },

  getMine: async (id: string) => {
    const response = await axios.get<{
      course: SelfCourse;
      enrollment: SelfCourseEnrollment;
    }>(`${baseUrl}self-courses/${id}`, { headers: authHeaders() });
    return response.data;
  },

  enroll: async (id: string) => {
    const response = await axios.post<{ enrollment: SelfCourseEnrollment }>(
      `${baseUrl}self-courses/${id}/enroll`,
      {},
      { headers: authHeaders() }
    );
    return response.data.enrollment;
  },

  completeLesson: async (courseId: string, lessonId: string) => {
    const response = await axios.post<{ enrollment: SelfCourseEnrollment }>(
      `${baseUrl}self-courses/${courseId}/lessons/${lessonId}/complete`,
      {},
      { headers: authHeaders() }
    );
    return response.data.enrollment;
  },

  submitLesson: async (
    courseId: string,
    lessonId: string,
    payload: { link: string; comment?: string }
  ) => {
    const response = await axios.post<{ enrollment: SelfCourseEnrollment }>(
      `${baseUrl}self-courses/${courseId}/lessons/${lessonId}/submit`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.enrollment;
  },

  submitQuiz: async (
    courseId: string,
    lessonId: string,
    answers: Record<string, string | string[]>
  ) => {
    const response = await axios.post<{
      enrollment: SelfCourseEnrollment;
      score: number;
      passed: boolean;
    }>(
      `${baseUrl}self-courses/${courseId}/lessons/${lessonId}/quiz`,
      { answers },
      { headers: authHeaders() }
    );
    return response.data;
  },

  listAdmin: async () => {
    const response = await axios.get<{ courses: SelfCourse[] }>(
      `${baseUrl}admin/self-courses`,
      { headers: authHeaders() }
    );
    return response.data.courses;
  },

  createAdmin: async (payload: { title: string; description?: string }) => {
    const response = await axios.post<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  updateAdmin: async (id: string, payload: Partial<SelfCourse>) => {
    const response = await axios.put<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses/${id}`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  addModule: async (id: string, payload: { title: string; subtitle?: string }) => {
    const response = await axios.post<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses/${id}/modules`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  addLesson: async (
    id: string,
    moduleId: string,
    payload: {
      title: string;
      type: SelfCourseLessonType;
      durationMinutes?: number;
      content?: string;
      videoUrl?: string;
    }
  ) => {
    const response = await axios.post<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses/${id}/modules/${moduleId}/lessons`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  updateLesson: async (
    id: string,
    moduleId: string,
    lessonId: string,
    payload: Partial<{
      title: string;
      type: SelfCourseLessonType;
      durationMinutes: number;
      content: string;
      videoUrl: string;
      passingScore: number;
      questions: SelfCourseQuizQuestion[];
    }>
  ) => {
    const response = await axios.patch<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses/${id}/modules/${moduleId}/lessons/${lessonId}`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  addLessonMaterials: async (
    id: string,
    moduleId: string,
    lessonId: string,
    files: File[]
  ) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await axios.post<{ course: SelfCourse }>(
      `${baseUrl}admin/self-courses/${id}/modules/${moduleId}/lessons/${lessonId}/materials`,
      formData,
      { headers: authHeaders() }
    );
    return response.data.course;
  },

  listEnrollments: async (id: string) => {
    const response = await axios.get<{
      course: SelfCourse;
      enrollments: SelfCourseEnrollment[];
    }>(`${baseUrl}admin/self-courses/${id}/enrollments`, {
      headers: authHeaders(),
    });
    return response.data;
  },

  reviewSubmission: async (
    courseId: string,
    enrollmentId: string,
    submissionId: string,
    payload: { score: number | ""; status: string; adminComment?: string }
  ) => {
    const response = await axios.patch<{ enrollment: SelfCourseEnrollment }>(
      `${baseUrl}admin/self-courses/${courseId}/enrollments/${enrollmentId}/submissions/${submissionId}`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.enrollment;
  },
};
